import { PAYMENT_METHODS } from '@wodi/domain';
import { z } from 'zod';

import { BILL_LINE_STATES } from './billing.ts';
import { reasonSchema } from './clinical-common.ts';
import { amountSchema, centsStringSchema, signedAmountSchema } from './money.ts';

// Inpatient billing (docs/plans/phase2-inpatient-billing.md §3): deposits, the running bill
// of a stay, interim and final bills, and packages. Money is integer cents in a string;
// stays are addressed by internal IDs only.

export const INPATIENT_BILL_KINDS = ['interim', 'final'] as const;
export type InpatientBillKind = (typeof INPATIENT_BILL_KINDS)[number];

export const STAY_BALANCE_STATES = ['owes', 'refund_due', 'settled'] as const;

const person = z.object({ id: z.uuid(), name: z.string() });

// --- Deposits -----------------------------------------------------------------------------------

/** A deposit pays no line, so a cash deposit names its amount as well as the cash handed over. */
export const depositTenderSchema = z.discriminatedUnion('method', [
  z.object({
    method: z.literal('cash'),
    amountCents: centsStringSchema,
    tenderedCents: centsStringSchema,
  }),
  z.object({
    method: z.enum(['mpesa', 'card', 'bank_transfer']),
    amountCents: centsStringSchema,
    /** The M-Pesa transaction code, card slip or bank reference. */
    reference: z.string().trim().min(3).max(40),
  }),
]);

export type DepositTender = z.infer<typeof depositTenderSchema>;

export const takeDepositBodySchema = z.object({
  clientRequestId: z.uuid(),
  tender: depositTenderSchema,
});

export const depositViewSchema = z.object({
  id: z.uuid(),
  paymentId: z.uuid(),
  receipt: z.object({ id: z.uuid(), number: z.string() }).nullable(),
  method: z.enum(PAYMENT_METHODS),
  reference: z.string().nullable(),
  amount: amountSchema,
  /** Applied to the bill by the final bill. */
  applied: amountSchema,
  /** Paid back to the patient. */
  refunded: amountSchema,
  /** Still with the facility: amount − applied − refunded. */
  held: amountSchema,
  receivedAt: z.iso.datetime(),
  cashier: person,
});

export type DepositView = z.infer<typeof depositViewSchema>;

// --- Packages -----------------------------------------------------------------------------------

const packageName = z.string().trim().min(1).max(120);

export const packageCoverBodySchema = z.object({
  serviceItemId: z.uuid(),
  /** Whole units covered; null for any quantity. */
  maxQuantity: z.number().int().min(1).max(100_000).nullable(),
});

export const createPackageBodySchema = z.object({
  name: packageName,
  /** The catalogue service the package is charged as; priced in the price lists. */
  serviceItemId: z.uuid(),
  description: z.string().trim().max(500).optional(),
  covers: z.array(packageCoverBodySchema).min(1).max(50),
});

export const updatePackageBodySchema = z
  .object({
    name: packageName.optional(),
    serviceItemId: z.uuid().optional(),
    description: z.string().trim().max(500).nullable().optional(),
    covers: z.array(packageCoverBodySchema).min(1).max(50).optional(),
    status: z.enum(['active', 'inactive']).optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'Nothing to change');

const itemRef = z.object({ id: z.uuid(), code: z.string(), name: z.string() });

export const packageViewSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.enum(['active', 'inactive']),
  item: itemRef,
  covers: z.array(
    z.object({
      serviceItemId: z.uuid(),
      code: z.string(),
      name: z.string(),
      maxQuantity: z.number().int().nullable(),
    }),
  ),
});

export type PackageView = z.infer<typeof packageViewSchema>;

export const packageListSchema = z.object({ items: z.array(packageViewSchema) });

export const applyPackageBodySchema = z.object({
  packageId: z.uuid(),
  clientRequestId: z.uuid().optional(),
});

export const removePackageBodySchema = z.object({ reason: reasonSchema });

export const stayPackageViewSchema = z.object({
  id: z.uuid(),
  packageId: z.uuid(),
  name: z.string(),
  item: itemRef,
  status: z.enum(['active', 'removed']),
  appliedAt: z.iso.datetime(),
  appliedBy: person,
  /** The package's own charge on the stay. */
  charge: z
    .object({
      id: z.uuid(),
      status: z.enum(['pending', 'billed', 'cancelled']),
      total: amountSchema.nullable(),
      patientAmount: amountSchema.nullable(),
    })
    .nullable(),
  /** How many of the stay's charges it replaced. */
  coveredCount: z.number().int().min(0),
  removedAt: z.iso.datetime().nullable(),
  removeReason: z.string().nullable(),
  /** Removing is allowed: the stay is admitted and the package charge isn't billed. */
  removable: z.boolean(),
});

export type StayPackageView = z.infer<typeof stayPackageViewSchema>;

// --- The stay's bill ----------------------------------------------------------------------------

export const stayTotalsSchema = z.object({
  charged: amountSchema,
  payerAmount: amountSchema,
  patientAmount: amountSchema,
  adjusted: amountSchema,
  credited: amountSchema,
  paid: amountSchema,
  owed: amountSchema,
  patientCredit: amountSchema,
  depositsReceived: amountSchema,
  depositsApplied: amountSchema,
  depositsRefunded: amountSchema,
  depositHeld: amountSchema,
  /** owed − credit: positive when the patient owes, negative when a refund is due. */
  balance: signedAmountSchema,
  balanceState: z.enum(STAY_BALANCE_STATES),
  unpricedCount: z.number().int().min(0),
});

export type StayTotals = z.infer<typeof stayTotalsSchema>;

export const inpatientBillSummarySchema = z.object({
  id: z.uuid(),
  kind: z.enum(INPATIENT_BILL_KINDS),
  number: z.string(),
  issuedAt: z.iso.datetime(),
  issuedBy: person,
  balance: signedAmountSchema,
});

export type InpatientBillSummary = z.infer<typeof inpatientBillSummarySchema>;

export const stayBillingSchema = z.object({
  admission: z.object({
    id: z.uuid(),
    status: z.enum(['admitted', 'discharged', 'cancelled']),
    visitId: z.uuid(),
    visitNumber: z.string(),
    patientId: z.uuid(),
    admittedAt: z.iso.datetime(),
    dischargedAt: z.iso.datetime().nullable(),
    /** What was still owed at discharge, after deposits; null before discharge. */
    dischargeBalance: amountSchema.nullable(),
  }),
  payer: z.object({ name: z.string(), kind: z.string() }),
  totals: stayTotalsSchema,
  deposits: z.array(depositViewSchema),
  packages: z.array(stayPackageViewSchema),
  bills: z.array(inpatientBillSummarySchema),
  finalBillId: z.uuid().nullable(),
});

export type StayBilling = z.infer<typeof stayBillingSchema>;

/**
 * What the discharge dialog shows a clinician (plan §1.1 item 7): the balance only, never the
 * bill's lines. Discharge never waits for it.
 */
export const stayBalanceSchema = z.object({
  balanceState: z.enum(STAY_BALANCE_STATES),
  amountOwed: amountSchema,
  refundDue: amountSchema,
  depositHeld: amountSchema,
  unpricedCount: z.number().int().min(0),
});

export type StayBalanceView = z.infer<typeof stayBalanceSchema>;

export const visitStayBillingSchema = z.object({ stay: stayBillingSchema.nullable() });

export const stayListItemSchema = z.object({
  admissionId: z.uuid(),
  visitId: z.uuid(),
  visitNumber: z.string(),
  patient: z.object({ id: z.uuid(), name: z.string(), patientNumber: z.string() }),
  status: z.enum(['admitted', 'discharged']),
  admittedAt: z.iso.datetime(),
  dischargedAt: z.iso.datetime().nullable(),
  depositHeld: amountSchema,
  balance: signedAmountSchema,
  finalBilled: z.boolean(),
});

export type StayListItem = z.infer<typeof stayListItemSchema>;

export const stayListSchema = z.object({ items: z.array(stayListItemSchema) });

export const takeDepositResultSchema = z.object({
  deposit: depositViewSchema,
  change: amountSchema.nullable(),
  replayed: z.boolean(),
  billing: stayBillingSchema,
});

export const issueInpatientBillResultSchema = z.object({
  bill: inpatientBillSummarySchema,
  billing: stayBillingSchema,
});

// --- The printed bill ---------------------------------------------------------------------------

export const inpatientBillLineSchema = z.object({
  chargeId: z.uuid(),
  description: z.string(),
  quantity: z.number().int(),
  occurredAt: z.iso.datetime(),
  total: amountSchema,
  payerAmount: amountSchema,
  patientAmount: amountSchema,
  state: z.enum(BILL_LINE_STATES),
  /** Why a cancelled line costs nothing, for example "Covered by package …". */
  note: z.string().nullable(),
});

export const inpatientBillViewSchema = z.object({
  id: z.uuid(),
  kind: z.enum(INPATIENT_BILL_KINDS),
  number: z.string(),
  issuedAt: z.iso.datetime(),
  issuedBy: person,
  printCount: z.number().int().min(0),
  facility: z.object({ name: z.string(), mflCode: z.string().nullable() }),
  patient: z.object({ name: z.string(), patientNumber: z.string() }),
  visitNumber: z.string(),
  admittedAt: z.iso.datetime(),
  dischargedAt: z.iso.datetime().nullable(),
  payer: z.string(),
  lines: z.array(inpatientBillLineSchema),
  totals: stayTotalsSchema,
});

export type InpatientBillView = z.infer<typeof inpatientBillViewSchema>;

export const inpatientBillPrintedSchema = z.object({ copyNumber: z.number().int().min(1) });
